import React from 'react'
import { Grid, Typography, Paper, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { printValue } from '../utils/PrintValue'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  paper: {
    padding: theme.spacing(2),
    color: 'rgba(0,0,0,0.9)',
    height: '100%'
  },
  descript: { fontSize: '0.8rem', color: '#545454' },
  descriptVal: { fontSize: '0.85rem' },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  }
}))

export default function AbsenceManagementMain(props) {
  const classes = useStyles()
  const { data, isLoading } = props

  const renderValue = (lookup, description, options = {}, secondary = null, secondType = null) => {
    return printValue(data, lookup, description, secondary, secondType, {
      ...options,
      classes: {
        description: classes.descript,
        value: classes.descriptVal
      }
    })
  }

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  // Check if data is empty or undefined
  const hasData = data && Object.keys(data).length > 0

  if (!hasData) {
    return (
      <div className={classes.loadingContainer}>
        <Typography variant="subtitle1">No data available</Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={1}
        direction='row'
        justifyContent='flex-start'
        alignItems='stretch'
      >
        {/* Dates */}
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Dates</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6} lg={6}>
                {renderValue('MASTR_ENTRY', 'Master Entry Date')}
                {renderValue('MSTR_ELIG_END', 'Accrual End Date', {type: 'date'})}
                {renderValue('MSTR_END', 'Master End Date')}
                {renderValue('TRANSFER_FLAG', 'Transfer Flag')}
                {renderValue('MSTR_OVERRIDE', 'Master Override')}
                {renderValue('SRV_BEGIN_DT', 'Service Begin Date')}
                {renderValue('ACCR_BEGIN_DT', 'Accrual Begin Date')}
                {renderValue('ACCR_START_DT', 'Accrual Start Date')}
                {renderValue('ACCR_LAST_DT', 'Accrual Last Date')}
              </Grid>
              <Grid item xs={12} sm={6} lg={6}>
                {renderValue('ALLOT_BEGIN_DT', 'Allotment Begin Date')}
                {renderValue('ALLOT_LAST_DT', 'Allotment Last Date')}
                {renderValue('ALLOT_POINT', 'Allotment Point')}
                {renderValue('ELIG_BEGIN_DT', 'Eligibility Begin Date')}
                {renderValue('ELIG_LAST_DT', 'Eligibility Last Date')}
                {renderValue('RESET_POINT', 'Reset Point')}
                {renderValue('RESET_LAST_DT', 'Reset Last Date')}
                {renderValue('LST_PER_DT_HRS', 'Last Period Date Hours')}
                {renderValue('LST_PER_DT_ERN', 'Last Period Date Earnings')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Balances */}
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Balances</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6} lg={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {renderValue('AVAIL_HRS_BAL', 'Available Hours Balance')}
                {renderValue('ACCR_HRS_BAL', 'Accrued Hours Balance')}
                {renderValue('ALLOT_HRS_BAL', 'Allotment Hours Balance')}
                {renderValue('RESRV_HRS_BAL', 'Reserved Hours Balance')}
              </Grid>
              <Grid item xs={12} sm={6} lg={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {renderValue('AVAIL_ERN_BAL', 'Available Earnings Balance')}
                {renderValue('ACCR_ERN_BAL', 'Accrued Earnings Balance')}
                {renderValue('ALLOT_ERN_BAL', 'Allotment Earnings Balance')}
                {renderValue('RESRV_ERN_BAL', 'Reserved Earnings Balance')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Payroll */}
        <Grid item xs={12}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Payroll</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {renderValue('OV_HRS_NEG_LMT', 'Override Hours Negative Limit')}
                {renderValue('OV_HRS_LINK_TB', 'Override Hours Link TB')}
                {renderValue('OV_HRS_PRO_ORD', 'Override Hours Processing Order')}
                {renderValue('OV_HRS_NEG_BAL', 'Override Hours Negative Balance')}
                {renderValue('OV_HRS_NEG_WEB', 'Override Hours Negative Web')}
                {renderValue('OV_HRS_UNPD_CD', 'Override Hours Unpaid Code')}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {renderValue('OV_ERN_NEG_LMT', 'Override Earnings Negative Limit')}
                {renderValue('OV_ERN_LINK_TB', 'Override Earnings Link TB')}
                {renderValue('OV_ERN_PRO_ORD', 'Override Earnings Processing Order')}
                {renderValue('OV_ERN_NEG_BAL', 'Override Earnings Negative Balance')}
                {renderValue('OV_ERN_NEG_WEB', 'Override Earnings Negative Web')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* GL Accounts */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>GL Accounts</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-start'
            >
              <Grid item xs={12} md={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {renderValue('HRS_ACCR_CO', 'Hours Accrual Company')}
                {renderValue('HRS_ACCR_UNIT', 'Hours Accrual Unit')}
                {renderValue('HRS_ACCR_ACCT', 'Hours Accrual Account')}
                {renderValue('HRS_ACCR_SUB', 'Hours Accrual Sub')}
                {renderValue('HRS_EXP_CO', 'Hours Expense Company')}
                {renderValue('HRS_EXP_UNIT', 'Hours Expense Unit')}
                {renderValue('HRS_EXP_ACCT', 'Hours Expense Account')}
                {renderValue('HRS_ACTIVITY', 'Hours Activity')}
                {renderValue('HRS_ACCT_CAT', 'Hours Account Category')}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {renderValue('ERN_ACCR_CO', 'Earnings Accrual Company')}
                {renderValue('ERN_ACCR_UNIT', 'Earnings Accrual Unit')}
                {renderValue('ERN_ACCR_ACCT', 'Earnings Accrual Account')}
                {renderValue('ERN_ACCR_SUB', 'Earnings Accrual Sub')}
                {renderValue('ERN_EXP_CO', 'Earnings Expense Company')}
                {renderValue('ERN_EXP_UNIT', 'Earnings Expense Unit')}
                {renderValue('ERN_EXP_ACCT', 'Earnings Expense Account')}
                {renderValue('ERN_ACTIVITY', 'Earnings Activity')}
                {renderValue('ERN_ACCT_CAT', 'Earnings Account Category')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Override Tables */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Override Tables</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Hours</Typography>
                {renderValue('OV_ACCR_HRS', 'Override Accrual Hours')}
                {renderValue('OV_ALLOT_HRS', 'Override Allotment Hours')}
                {renderValue('OV_BAL_HRS', 'Override Balance Hours')}
                {renderValue('OV_ACRLIM_HRS', 'Override Accrual Limit Hours')}
                {renderValue('OV_CARRY_HRS_T', 'Override Carryover Hours T')}
                {renderValue('OV_HRS_LIA', 'Override Hours Liability')}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle1'>Earnings</Typography>
                {renderValue('OV_ACCR_ERN', 'Override Accrual Earnings')}
                {renderValue('OV_ALLOT_ERN', 'Override Allotment Earnings')}
                {renderValue('OV_BAL_ERN', 'Override Balance Earnings')}
                {renderValue('OV_ACRLIM_ERN', 'Override Accrual Limit Earnings')}
                {renderValue('OV_CARRY_ERN_T', 'Override Carryover Earnings T')}
                {renderValue('OV_ERN_LIA', 'Override Earnings Liability')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Carryover */}
        <Grid item xs={12}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Carryover</Typography>
            <Grid
                container
                spacing={0}
                direction='row'
                justifyContent='space-evenly'
                alignItems='flex-start'
            >
              <Grid item xs={12}>
                {renderValue('CARRYOVER_END', 'Carryover End')}
                {renderValue('CARRY_HRS_BAL', 'Carryover Hours Balance')}
                {renderValue('CARRY_ERN_BAL', 'Carryover Earnings Balance')}
                {renderValue('OV_CARRY_HRS_A', 'Override Carryover Hours A')}
                {renderValue('OV_CARRY_ERN_A', 'Override Carryover Earnings A')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

      </Grid>
    </div>
  )
}